import Icon from 'components/Icon';
import Text from 'components/Text';
import { TEXT_STRING } from '../../constants';
import { EPaymentMethod } from 'types';
import Button from 'components/Button';
import { useMemo } from 'react';
import { useUserStore } from 'store';

type Props = {
  onChooseMethod: (paymentMethod: EPaymentMethod) => void;
  loading?: boolean;
  paymentMethod: EPaymentMethod;
  onSubmit: () => void;
  price: number;
  allowPaymentMethod?: EPaymentMethod[];
};

const MethodPayment = ({
  onChooseMethod,
  loading,
  paymentMethod,
  onSubmit,
  price,
  allowPaymentMethod,
}: Props) => {
  const { user } = useUserStore();

  const methodOptions = useMemo(
    () => [
      {
        label: TEXT_STRING.PAYMENT.CASH,
        icon: 'money',
        method: EPaymentMethod.CASH,
        visible: allowPaymentMethod?.includes(EPaymentMethod.CASH),
      },
      {
        label: TEXT_STRING.PAYMENT.POINT_ASSET,
        icon: 'teco',
        method: EPaymentMethod.POINT,
        visible: allowPaymentMethod?.includes(EPaymentMethod.POINT),
        disabled: user?.isLimited,
      },
      {
        label: TEXT_STRING.PAYMENT.CARD,
        icon: 'creditCard',
        method: EPaymentMethod.CARD,
        disabled: !price,
        visible: allowPaymentMethod?.includes(EPaymentMethod.CARD),
      },
      {
        label: TEXT_STRING.PAYMENT.BANK,
        icon: 'bank',
        method: EPaymentMethod.BANK,
        disabled: !price,
        visible: allowPaymentMethod?.includes(EPaymentMethod.BANK),
      },
      {
        label: TEXT_STRING.PAYMENT.POSTPAY,
        icon: 'postpay',
        method: EPaymentMethod.POSTPAY,
        disabled: !price,
        visible: allowPaymentMethod?.includes(EPaymentMethod.POSTPAY),
      },
    ],
    [allowPaymentMethod, price, user?.isLimited],
  );

  return (
    <>
      <div className="relative grid grid-cols-2 gap-4 py-8">
        {methodOptions.map((item) => {
          const isActive = paymentMethod === item.method;
          return item.visible ? (
            <div
              key={item.method}
              className={`relative basis-1/2 border rounded-md cursor-pointer ${
                isActive ? 'border-primary bg-primary-50' : ''
              } ${item.disabled && 'pointer-events-none opacity-50'}`}
              onClick={() => {
                onChooseMethod(item.method);
              }}
            >
              <div
                className={`w-5 absolute top-2 right-2 aspect-square rounded-full ${
                  isActive
                    ? 'bg-primary ring-primary border-4 border-solid border-white'
                    : 'ring-neutral-200'
                } ring-1 cursor-pointer ml-auto`}
              ></div>
              <div className="h-full flex items-center gap-3 px-6 py-6">
                <div className="w-7 h-7">
                  <Icon icon={item.icon} />
                </div>
                <Text>{item.label}</Text>
              </div>
            </div>
          ) : null;
        })}
        {[EPaymentMethod.POSTPAY, EPaymentMethod.BANK].includes(
          paymentMethod,
        ) && (
          <Text
            fontSize={12}
            fontWeight="font-medium"
            textColor="text-red-500"
            className="tracking-wide absolute bottom-2.5 right-2"
          >
            {TEXT_STRING.PAYMENT.POSTPAY_ANNOUNCE}
          </Text>
        )}
      </div>
      <Button
        block
        onClick={onSubmit}
        loading={loading}
        disabled={loading || !allowPaymentMethod?.includes(paymentMethod)}
      >
        {TEXT_STRING.COMMON.CONTINUE}
      </Button>
    </>
  );
};

export default MethodPayment;
